import { ArrowUpRight, Copy, GitBranch, Globe, Monitor, MousePointerClick, PanelsTopLeft } from 'lucide-react';
import { Tooltip, useToast } from '@vd/ui';
import { cleanClientHints, parseUserAgent, placedSizeLabel, sessionSummary, visibleControls, userAgentSummary, type CaptureBrowser, type PlacedSize, type Ticket } from '@vd/types';
import { copyToClipboard } from '../../lib/clipboard';
import styles from './TicketDetail.module.css';

/**
 * The browser a capture was taken in, as one line (TOADO-907):
 * "Chrome 141 · Windows · 1920 × 1080 at 1.25× · Dark mode · Time zone America/New_York · Reduced motion".
 * Only what was recorded; null when nothing was.
 */
export function captureBrowserFacts(browser: CaptureBrowser | undefined): { summary: string | null; line: string | null } {
  if (!browser) return { summary: null, line: null };
  // The client hints a Chromium browser gave, checked again, name the real versions (TOADO-907).
  const hints = cleanClientHints(browser);
  const parsed = parseUserAgent(browser.userAgent, hints);
  const name = (part: { name: string; version?: string } | undefined) => part && [part.name, part.version].filter(Boolean).join(' ');
  const size = browser.viewport
    ? `${browser.viewport.width} × ${browser.viewport.height}${browser.devicePixelRatio && browser.devicePixelRatio !== 1 ? ` at ${browser.devicePixelRatio}×` : ''}`
    : null;
  const scheme = browser.colorScheme === 'dark' ? 'Dark mode' : browser.colorScheme === 'light' ? 'Light mode' : null;
  const parts = [
    name(parsed.browser), name(parsed.os), hints.model, size, scheme,
    browser.timezone ? `Time zone ${browser.timezone}` : null,
    browser.reducedMotion ? 'Reduced motion' : null,
  ].filter((part): part is string => !!part);
  return { summary: userAgentSummary(parsed), line: parts.length > 0 ? parts.join(' · ') : null };
}

/** Captured addresses and page labels are untrusted text, never markup. */
export function TicketCaptureMeta({ ticket, placedAt, onOpenSession }: { ticket: Ticket; placedAt?: PlacedSize; onOpenSession?: () => void }) {
  const toast = useToast();
  let source: URL | undefined;
  try { source = ticket.sourceUrl ? new URL(ticket.sourceUrl) : undefined; } catch { /* Display malformed legacy addresses as text. */ }
  const placement = [
    ticket.screen?.label && `Placed on: ${ticket.screen.label}`,
    placedAt && `Placed at ${placedSizeLabel(placedAt)}`,
  ].filter(Boolean).join('\n');
  const copy = async (value: string, title: string) => {
    if (await copyToClipboard(value)) toast.push({ variant: 'success', title });
    else toast.push({ variant: 'danger', title: 'Could not copy. Please try again.' });
  };
  const browser = ticket.capture?.browser;
  const facts = captureBrowserFacts(browser);
  // The visit before the report (TOADO-911), which opens the Session window.
  const trail = sessionSummary(ticket.capture?.session);

  // Everything Capture details shows, and the full user agent, for pasting
  // into a bug report or a chat with an agent. Each line is made one line,
  // with any control or bidi character shown as its code: the user agent and
  // the screen label are written by the page, and a line break in one made a
  // copied "Branch: production" line of its own (Codex, round 1). The user
  // agent is quoted, and says whose it is.
  const rawAgent = browser?.userAgent ? visibleControls(browser.userAgent) : undefined;
  const agentSource = browser?.uaSource === 'request' ? 'from the upload request' : 'as the browser reported it';
  const allDetails = [
    facts.line, trail, ticket.screen?.label && `Placed on: ${ticket.screen.label}`,
    placedAt && `Placed at ${placedSizeLabel(placedAt)}`,
    ticket.branchName && `Branch: ${ticket.branchName}`,
    rawAgent && `User agent (${agentSource}): "${rawAgent}"`,
  ].filter((line): line is string => !!line).map(visibleControls).join('\n');
  if (!ticket.sourceUrl && !placement && !ticket.branchName && !facts.line && !rawAgent && !trail) return null;
  return (
    <div className={styles.captureMeta}>
      {ticket.sourceUrl && (
        <div className={styles.sourceLine}>
          <Tooltip multiline content={<span style={{ whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' }}>{[ticket.sourceUrl, placement].filter(Boolean).join('\n')}</span>}>
            <button type="button" className={`${styles.urlChip} ${styles.sourceAddress}`} data-testid="ticket-source-url"
              onClick={() => void copy(ticket.sourceUrl, 'Link copied')} aria-label="Copy source page address">
              <Globe size={11} /><span>{source ? source.host + source.pathname : ticket.sourceUrl}</span>
            </button>
          </Tooltip>
          <button type="button" className={styles.urlIcon} aria-label="Copy page URL"
            onClick={() => void copy(ticket.sourceUrl, 'Link copied')}><Copy size={12} /></button>
          {source && /^https?:$/.test(source.protocol) && (
            <a className={styles.urlIcon} href={ticket.sourceUrl} target="_blank" rel="noopener noreferrer"
              aria-label="Open this page in a new tab" data-testid="ticket-source-open"><ArrowUpRight size={12} /></a>
          )}
        </div>
      )}
      {(placement || ticket.branchName || facts.line || rawAgent || trail) && (
        // A different ticket starts collapsed even during in-place navigation.
        <details className={styles.captureDetails} key={ticket.id}>
          <summary>Capture details{facts.summary ? ` · ${facts.summary}` : ''}</summary>
          <div className={styles.captureFacts}>
            {facts.line && <span data-testid="ticket-browser" title={rawAgent}><Monitor size={11} />{facts.line}</span>}
            {trail && <button type="button" className={styles.urlChip} data-testid="ticket-session-summary" onClick={onOpenSession} disabled={!onOpenSession}
              title="Open the Session window"><MousePointerClick size={11} /><span>{trail}</span></button>}
            {ticket.screen?.label && <span data-testid="ticket-screen"><PanelsTopLeft size={11} />Placed on: {ticket.screen.label}</span>}
            {placedAt && <span data-testid="ticket-placed-size"><Monitor size={11} />Placed at {placedSizeLabel(placedAt)}</span>}
            {ticket.branchName && <button type="button" className={styles.urlChip} title={`Branch: ${ticket.branchName} (click to copy)`}
              onClick={() => void copy(ticket.branchName!, 'Branch name copied')}><GitBranch size={11} /><span>{ticket.branchName}</span></button>}
            {rawAgent && (
              <details data-testid="ticket-user-agent">
                <summary>Show raw</summary>
                <code style={{ overflowWrap: 'anywhere' }}>{rawAgent}</code>
                <span className={styles.captureAgentSource}>User agent {agentSource}</span>
              </details>
            )}
            <button type="button" className={styles.urlChip} data-testid="ticket-copy-details"
              onClick={() => void copy(allDetails, 'Capture details copied')}><Copy size={11} /><span>Copy all</span></button>
          </div>
        </details>
      )}
    </div>
  );
}
